import { DisplayContainer } from 'components/Support/DisplayContainer/DisplayContainer';

import { BottomSheet } from 'components/Toolkit/BottomSheet/BottomSheet';
import { MakeModelProps } from 'components/SearchPage/features/makeModel/MakeModel.typed';
import { useMakeModelContext } from 'components/SearchPage/features/makeModel/MakeModelContext';

import { AddButton } from 'components/SearchPage/features/makeModel/components/AddButton';
import { MakeFilter } from 'components/SearchPage/features/makeModel/components/MakeFilter';
import { InputButton } from 'components/Toolkit/InputButton/InputButton';

import { Spacer } from 'helpers/genericStyles';
import { useMakeModel } from 'components/SearchPage/features/makeModel/MakeModel.hook';
import { Selected } from 'components/SearchPage/features/makeModel/components/Selected/Selected';
import { ModelFilter } from './components/ModelFilter';
import { Button } from 'components/Toolkit/Button';

import * as Styled from './MakeModel.styled';

const MakeModel = (props: MakeModelProps) => {
  // For desktop the dropdowns should not exceed 600px
  const DROPDOWN_MAX_HEIGHT = '600px';
  const {
    mWebConfig,
    webConfig,
    selectMake: onSelectMake,
    selectModel: onSelectModel,
    maximumAmountOfMakeModelGroupsToBeDisplayed,
    onAddSelection,
    makeOptions,
    heightVariant,
  } = props;

  const { isMakeModelBottomSheetOpen, makeModels } = useMakeModelContext();

  const {
    addMake,
    clearMake,
    openBottomSheet,
    closeBottomSheet,
    makeValue,
    selectedModelOptions,
    addModel,
    clearModels,
    modelPlaceholder,
    editableMakeModelGroupAvailable,
    isAddAnotherDisabled,
    isModelFilterDisabled,
    mappedMakeOptions,
    isModelSheetExpanded,
    setIsModelSheetExpanded,
  } = useMakeModel({
    onSelectMake,
    onSelectModel,
    maximumAmountOfMakeModelGroupsToBeDisplayed,
    makeOptions,
  });

  return (
    <div data-testid="make-model-dropdowns">
      {makeModels && makeModels.length > 0 && (
        <Selected
          makeModels={makeModels}
          clearSelection={clearMake}
          heightVariant={heightVariant}
        />
      )}
      {editableMakeModelGroupAvailable && (
        <>
          <DisplayContainer config={mWebConfig} data-testid="mobile-make-model">
            <InputButton
              isActive={isMakeModelBottomSheetOpen}
              value={makeValue}
              onClick={openBottomSheet}
              placeholder="Make"
              heightVariant={heightVariant}
              dataTestId="mobile-make-input-button"
            />
            <BottomSheet
              headerText={makeValue}
              askToClose={closeBottomSheet}
              isOpen={isMakeModelBottomSheetOpen}
              onApply={closeBottomSheet}
              height="75vh"
            >
              {isMakeModelBottomSheetOpen && (
                <MakeFilter
                  value={makeValue}
                  heightVariant={heightVariant}
                  options={mappedMakeOptions}
                  addMake={addMake}
                />
              )}
            </BottomSheet>
            <Spacer marginBottom="M16" />
            <Styled.SInputButton
              placeholder={modelPlaceholder}
              isActive={isModelSheetExpanded}
              value={modelPlaceholder}
              heightVariant={heightVariant}
              isDisabled={isModelFilterDisabled}
              onClick={() => setIsModelSheetExpanded(true)}
              dataTestId="mobile-model-input-button"
            />
            <BottomSheet
              headerText={modelPlaceholder}
              askToClose={() => setIsModelSheetExpanded(false)}
              isOpen={isModelSheetExpanded}
              height="75vh"
              onApply={() => setIsModelSheetExpanded(false)}
              buttonA={
                <Button ofType="SECONDARY" onClick={clearModels}>
                  Clear
                </Button>
              }
              buttonB={
                <Button onClick={() => setIsModelSheetExpanded(false)}>
                  Done
                </Button>
              }
            >
              {isModelSheetExpanded && (
                <ModelFilter
                  addModel={addModel}
                  clearModels={clearModels}
                  selected={selectedModelOptions}
                  placeholder={`Search ${makeValue} models`}
                  heightVariant={heightVariant}
                  value={modelPlaceholder}
                  isDisabled={isModelFilterDisabled}
                />
              )}
            </BottomSheet>
          </DisplayContainer>
          <DisplayContainer config={webConfig} data-testid="desktop-make-model">
            <MakeFilter
              value={makeValue}
              heightVariant={heightVariant}
              options={mappedMakeOptions}
              addMake={addMake}
              maxHeight={DROPDOWN_MAX_HEIGHT}
              showLabel
            />
            <Spacer marginBottom="M16" />
            <ModelFilter
              addModel={addModel}
              clearModels={clearModels}
              selected={selectedModelOptions}
              placeholder={`Search ${makeValue} models`}
              value={modelPlaceholder}
              isDisabled={isModelFilterDisabled}
              heightVariant={heightVariant}
              maxHeight={DROPDOWN_MAX_HEIGHT}
              showLabel
              showFooter
            />
          </DisplayContainer>
          <Spacer marginBottom="M16" />
          <Spacer marginBottom="M16" />
          <AddButton
            disabled={isAddAnotherDisabled}
            onClick={onAddSelection}
            size={heightVariant === 'LARGE' ? 'LARGE' : 'SMALL'}
          />
        </>
      )}
    </div>
  );
};

export { MakeModel };
